import { useMemo } from 'react'

/**
 * Generic client-side pagination hook that handles filtering and slicing data.
 *
 * Functional Core approach: Pure pagination logic separated from component concerns.
 *
 * @param data - The full dataset to paginate
 * @param page - Current page number (1-indexed)
 * @param pageSize - Number of items per page
 * @param filterFn - Optional filter function to exclude items that shouldn't be shown
 * @returns Object containing paginated data and total count
 *
 * @example
 * const { paginatedData, count } = useClientSidePagination(
 *   assessments,
 *   page,
 *   pageSize,
 *   (item) => shouldShowItem(item)
 * )
 */
export function useClientSidePagination<T>(
  data: T[] | undefined,
  page: number,
  pageSize: number,
  filterFn?: (item: T) => boolean,
): { paginatedData: T[]; count: number } {
  return useMemo(() => {
    if (!data) {
      return { paginatedData: [], count: 0 }
    }

    // Apply filter if provided
    const filteredData = filterFn ? data.filter(filterFn) : data

    // Calculate pagination
    const startIndex = (page - 1) * pageSize
    const endIndex = startIndex + pageSize
    const paginatedData = filteredData.slice(startIndex, endIndex)

    return {
      paginatedData,
      count: filteredData.length,
    }
  }, [data, page, pageSize, filterFn])
}
